import { AppSidebar } from "@/components/app-sidebar"
import { ProtectedRoute } from "@/components/protected-route"
import { Breadcrumb, BreadcrumbItem, BreadcrumbLink, BreadcrumbList, BreadcrumbPage, BreadcrumbSeparator } from "@/components/ui/breadcrumb"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { SidebarInset, SidebarProvider, SidebarTrigger } from "@/components/ui/sidebar"
import { app, useAppStore } from "@/lib/app"
import { Agent } from "@/pages/flow/agent"
import { Edit } from "lucide-react"
import React, { useState } from 'react'
import { Route, Routes, useLocation, useNavigate } from "react-router-dom"
import { ContextualPage } from "./page/pagecontext"
import { pages } from "@/lib/pages"
import { usePermissions } from "@/lib/hooks/usePermissions"
import { AssistantProvider } from "@/components/assistant/AssistantProvider"
import { AssistantPanel } from "@/components/assistant/AssistantPanel"

const routeNames: Record<string, string> = {
  '/': 'Agents',
  '/playground': 'Playground',
  '/moderation': 'Moderation',
  '/simulator': 'Simulator',
  '/analysis': 'Analysis',
  '/dashboard': 'Dashboard',
  '/knowledge/collections': 'Collections',
  '/knowledge/collections/:id': 'Collection Details',
  '/knowledge/sources': 'Sources',
  '/knowledge/sources/:id': 'Source Details',
  '/agent/:id': 'Agent Details',
  '/settings/projects': 'Settings / Projects',
  '/settings/teams': 'Settings / Teams',
  '/settings/billing': 'Settings / Billing',
  '/settings/usage': 'Settings / Usage',
  '/settings/roles': 'Settings / Access Control',
  '/settings/users': 'Settings / Users',
  '/settings/apikeys': 'Settings / Api Keys',
  '/settings/models': 'Settings / Models',
  '/settings/profile': 'Settings / Profile',
}

function NavigationLink({ to, children }: { to: string; children: React.ReactNode }) {
  const navigate = useNavigate()
  return (
    <BreadcrumbLink onClick={() => navigate(to, { replace: true })}>
      {children}
    </BreadcrumbLink>
  )
}

export function AppLayout() {
  const location = useLocation()
  const currentPath = location.pathname
  const { agent } = useAppStore()
  const { canRead } = usePermissions()
  const [editingTitle, setEditingTitle] = useState(agent?.title || '')
  React.useEffect(() => {
    if (agent?.title) {
      setEditingTitle(agent.title)
    }
  }, [agent?.id])

  // Breadcrumb logic for menu/submenu
  const breadcrumbItems: { name: string; to?: string }[] = [
    { name: 'Home', to: '/' },
  ]
  if (currentPath.startsWith('/agent/')) {
    breadcrumbItems.push({ name: 'Agents', to: '/' })
    // Ensure we have a proper agent title, fallback to 'Agent' if empty
    const agentTitle = agent?.title?.trim() || 'Agent'
    breadcrumbItems.push({ name: agentTitle })
  } else if (currentPath.startsWith('/collections') || currentPath.startsWith('/sources')) {
    breadcrumbItems.push({ name: 'Knowledge', to: '/collections' })
    if (currentPath === '/collections') {
      breadcrumbItems.push({ name: 'Collections' })
    } else if (currentPath === '/sources') {
      breadcrumbItems.push({ name: 'Sources' })
    }
  } else if (currentPath.startsWith('/settings/')) {
    breadcrumbItems.push({ name: 'Settings', to: '/settings/projects' })
    if (currentPath === '/settings/projects') {
      breadcrumbItems.push({ name: 'Projects' })
    } else if (currentPath === '/settings/teams') {
      breadcrumbItems.push({ name: 'Teams' })
    } else if (currentPath === '/settings/billing') {
      breadcrumbItems.push({ name: 'Billing' })
    } else if (currentPath === '/settings/usage') {
      breadcrumbItems.push({ name: 'Usage' })
    } else if (currentPath === '/settings/roles') {
      breadcrumbItems.push({ name: 'Access Control' })
    } else if (currentPath === '/settings/users') {
      breadcrumbItems.push({ name: 'Users' })
    } else if (currentPath === '/settings/apikeys') {
      breadcrumbItems.push({ name: 'Api Keys' })
    } else if (currentPath === '/settings/models') {
      breadcrumbItems.push({ name: 'Models' })
    } else if (currentPath === '/settings/profile') {
      breadcrumbItems.push({ name: 'Profile' })
    }
  } else if (routeNames[currentPath]) {
    breadcrumbItems.push({ name: routeNames[currentPath] })
  }

  return <AssistantProvider><SidebarProvider>
    <AppSidebar />
    <SidebarInset className="max-h-screen!">
      <header className="flex h-8 shrink-0 items-center gap-2 transition-[width,height] ease-linear group-has-data-[collapsible=icon]/sidebar-wrapper:h-12">
        <div className="flex items-center gap-2 ">
          <SidebarTrigger className="-ml-1" />
          <Breadcrumb>
            <BreadcrumbList>
              {breadcrumbItems.map((item, idx) => (
                <React.Fragment key={item.name}>
                  <BreadcrumbItem>
                    {idx !== breadcrumbItems.length - 1 && item.to ? (
                      <NavigationLink to={item.to}>{item.name}</NavigationLink>
                    ) : (
                      currentPath.startsWith('/agent/') && idx === breadcrumbItems.length - 1 && canRead('agents') ? (
                        <div className="flex items-center gap-1">
                          <Input
                            value={editingTitle}
                            onChange={(e) => setEditingTitle(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                e.currentTarget.blur()
                              }
                            }}
                            onBlur={() => {
                              if (agent && editingTitle !== agent.title) {
                                agent.title = editingTitle
                              }
                            }}
                            className="text-sm font-semibold w-64 border border-gray-200 bg-transparent p-1 focus-visible:ring-1 focus-visible:ring-blue-500 hover:bg-gray-50 rounded transition-colors"
                            placeholder="Agent Title"
                            autoFocus={false}
                          />
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-gray-500 hover:text-gray-700"
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                        </div>
                      ) : (
                        <BreadcrumbPage>{item.name}</BreadcrumbPage>
                      )
                    )}
                  </BreadcrumbItem>
                  {idx < breadcrumbItems.length - 1 && <BreadcrumbSeparator />}
                </React.Fragment>
              ))}
            </BreadcrumbList>
          </Breadcrumb>
        </div>
      </header>

      {/* <div className="bg-white min-h-screen rounded-sm md:min-h-min flex flex-col border border-border" > */}
      {/* The assistant docks beside the page and stays mounted across routes */}
      <div className="flex h-full max-h-full min-h-0 gap-2">
      <div className="bg-white flex min-w-0 flex-1 flex-col border border-border rounded-xl overflow-hidden h-full max-h-full">
        <Routes>
          {/* <Route path="/playground" element={
            <ProtectedRoute resource="playground" action="read">
              <Chat />
            </ProtectedRoute>
          } /> */}
          <Route path="/agent/:id" element={
            <ProtectedRoute resource="agents" action="read">
              <div className="flex flex-1 flex-col">
                <Agent />
              </div>
            </ProtectedRoute>
          } />

          {Object.values(pages).filter((page: any) => page.route).map((page) => <Route key={page.route} path={page.route} element={<ContextualPage config={page}><page.component /></ContextualPage>} />)}

        </Routes>
      </div>
      <AssistantPanel />
      </div>
    </SidebarInset>
  </SidebarProvider></AssistantProvider>

}
